import React, { useEffect, useState, useCallback } from "react";
import "../../Styles/GalleryTable.css";
import { useNavigate } from "react-router-dom";
import { galleryAPI, fileAPI } from "../../api/index"; // adjust path to match your project
import { CATEGORY_OPTIONS } from "./GalleryForm"; // reuse the same list — keeps values in sync
import { resolveMediaUrl } from "../../utils/mediaUrl"; // adjust path to match your project

const categoryValueToLabel = (value) =>
  CATEGORY_OPTIONS.find((c) => c.value === value)?.label || value;

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Backend gives "YYYY-MM-DD" (Pydantic `date`). Display it the way the UI expects.
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

const PAGE_SIZE = 10;

function GalleryTable() {
  const navigate = useNavigate();

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [status, setStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- IMAGE PREVIEW (in-page popup) ----------
  const [previewItem, setPreviewItem] = useState(null);
  const openPreview = (item) => setPreviewItem(item);
  const closePreview = () => setPreviewItem(null);

  // ---------- FETCH ----------
  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await galleryAPI.getAll({});
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setPhotos(data);
    } catch (err) {
      console.error("Failed to load gallery photos:", err);
      setError(
        err.response?.data?.detail || "Could not load gallery photos."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Close image preview with Escape key
  useEffect(() => {
    if (!previewItem) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [previewItem]);

  const resetFilters = () => {
    setSearch("");
    setCategoryFilter("All Categories");
    setStatus("All Status");
    setCurrentPage(1);
  };

  // ---------- SEARCH + FILTER (client-side) ----------
  const filteredPhotos = photos.filter((item) => {
    const searchLower = search.trim().toLowerCase();
    const matchesSearch =
      searchLower === "" ||
      (item.title || "").toLowerCase().includes(searchLower);

    const matchesCategory =
      categoryFilter === "All Categories" ||
      item.category === categoryFilter.toLowerCase();

    const matchesStatus =
      status === "All Status" ||
      (item.status || "").toLowerCase() === status.toLowerCase();

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredPhotos.length / PAGE_SIZE));
  const pageItems = filteredPhotos.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ---------- EDIT LOGIC ----------
  const openEditModal = (item) => {
    setEditItem({ ...item });
    setEditFile(null);
    setEditPreview(item.photo_url || null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditFile(null);
    setEditPreview(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditImageChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setEditFile(selected);
      setEditPreview(URL.createObjectURL(selected));
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      let photoUrl = editItem.photo_url;

      if (editFile) {
        const uploadRes = await fileAPI.upload(editFile, "gallery");
        photoUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url ||
          photoUrl;
      }

      await galleryAPI.update(editItem.id, {
        title: editItem.title,
        category: editItem.category,
        caption: editItem.caption || null,
        status: editItem.status,
        photo_url: photoUrl,
      });

      await fetchPhotos();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update photo:", err);
      alert(
        err.response?.data?.detail || "Something went wrong while saving changes."
      );
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- DELETE LOGIC ----------
  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await galleryAPI.delete(deleteId);
      await fetchPhotos();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete photo:", err);
      alert(err.response?.data?.detail || "Could not delete this photo.");
    } finally {
      setDeleting(false);
    }
  };

  // ---------- STATS ----------
  const totalPhotos = photos.length;
  const totalCategories = new Set(photos.map((p) => p.category)).size;
  const totalViews = photos.reduce((sum, p) => sum + (p.views || 0), 0);

  const recentlyAdded = photos.filter((p) => {
    if (!p.date_added) return false;
    const today = new Date();
    const [year, month] = p.date_added.split("-").map(Number);
    return month === today.getMonth() + 1 && year === today.getFullYear();
  }).length;

  return (
    <div className="gallery123-page">
      {/* Header */}
      <div className="gallery123-header">
        <div className="gallery123-header-left">
          <div className="gallery123-page-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <h2 className="gallery123-title">Photo Gallery</h2>
            <p className="gallery123-subtitle">
              Manage and organize all your gallery photos
            </p>
          </div>
        </div>

        <button
          className="gallery123-add-btn"
          onClick={() => navigate("/dashboard/GalleryForm")}
        >
          <span className="gallery123-add-icon">+</span> Add Photo
        </button>
      </div>

      {error && <div className="gallery123-no-data">{error}</div>}

      {/* Stat Cards */}
      <div className="gallery123-stats-grid">
        <div className="gallery123-stat-card">
          <div className="gallery123-stat-icon gallery123-stat-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="gallery123-stat-info">
            <span className="gallery123-stat-label">Total Photos</span>
            <span className="gallery123-stat-value">{totalPhotos}</span>
            <span className="gallery123-stat-sub">All time photos</span>
          </div>
        </div>

        <div className="gallery123-stat-card">
          <div className="gallery123-stat-icon gallery123-stat-icon-purple">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M4 6h16M4 12h16M4 18h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="gallery123-stat-info">
            <span className="gallery123-stat-label">Categories</span>
            <span className="gallery123-stat-value">{totalCategories}</span>
            <span className="gallery123-stat-sub">Active categories</span>
          </div>
        </div>

        <div className="gallery123-stat-card">
          <div className="gallery123-stat-icon gallery123-stat-icon-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="gallery123-stat-info">
            <span className="gallery123-stat-label">Recently Added</span>
            <span className="gallery123-stat-value">{recentlyAdded}</span>
            <span className="gallery123-stat-sub">This month</span>
          </div>
        </div>

        <div className="gallery123-stat-card">
          <div className="gallery123-stat-icon gallery123-stat-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
          <div className="gallery123-stat-info">
            <span className="gallery123-stat-label">Total Views</span>
            <span className="gallery123-stat-value">
              {totalViews.toLocaleString()}
            </span>
            <span className="gallery123-stat-sub">All time views</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="gallery123-filters">
        <div className="gallery123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by photo title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="gallery123-select"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Categories</option>
          {CATEGORY_OPTIONS.map((cat) => (
            <option key={cat.value}>{cat.label}</option>
          ))}
        </select>

        <select
          className="gallery123-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>

        <button className="gallery123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Reset Filters
        </button>
      </div>

      {/* Table */}
      <div className="gallery123-table-wrapper">
        <table className="gallery123-table table table-hover">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Photo</th>
              <th>Category</th>
              <th>Date Added</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="gallery123-no-data">
                  Loading...
                </td>
              </tr>
            ) : pageItems.length === 0 ? (
              <tr>
                <td colSpan="6" className="gallery123-no-data">
                  No photos found.
                </td>
              </tr>
            ) : (
              pageItems.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>

                  <td>
                    <div className="gallery123-photo-cell">
                      <button
                        type="button"
                        className="gallery123-thumb-btn"
                        onClick={() => openPreview(item)}
                        title="View photo"
                      >
                        <img
                          src={resolveMediaUrl(item.photo_url)}
                          alt={item.title}
                          className="gallery123-thumb"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%23e5e7eb'/%3E%3C/svg%3E";
                          }}
                        />
                      </button>
                      <span className="gallery123-photo-title">
                        {item.title}
                      </span>
                    </div>
                  </td>

                  <td>
                    <span className="gallery123-category-badge">
                      {categoryValueToLabel(item.category)}
                    </span>
                  </td>

                  <td>
                    <div className="gallery123-date-cell">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                      {formatDisplayDate(item.date_added)}
                    </div>
                  </td>

                  <td>
                    <span
                      className={`gallery123-status-badge ${
                        item.status === "active"
                          ? "gallery123-status-active"
                          : "gallery123-status-inactive"
                      }`}
                    >
                      {capitalize(item.status)}
                    </span>
                  </td>

                  <td>
                    <button
                      className="gallery123-edit-btn"
                      onClick={() => openEditModal(item)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Edit
                    </button>

                    <button
                      className="gallery123-delete-btn"
                      onClick={() => openDeleteModal(item.id)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="gallery123-pagination-bar">
        <span className="gallery123-pagination-info">
          Showing {pageItems.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
          {" "}to{" "}
          {(currentPage - 1) * PAGE_SIZE + pageItems.length} of{" "}
          {filteredPhotos.length} photos
        </span>

        <div className="gallery123-pagination-controls">
          <button
            className="gallery123-page-arrow"
            onClick={() => handlePageChange(currentPage - 1)}
          >
            &lt;
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`gallery123-page-num ${
                currentPage === p ? "gallery123-active" : ""
              }`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <button
            className="gallery123-page-arrow"
            onClick={() => handlePageChange(currentPage + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* ---------------- IMAGE PREVIEW POPUP ---------------- */}
      {previewItem && (
        <div className="gallery123-modal-overlay" onClick={closePreview}>
          <div
            className="gallery123-modal gallery123-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="gallery123-modal-header">
              <h3>{previewItem.title}</h3>
              <button className="gallery123-modal-close" onClick={closePreview}>
                &times;
              </button>
            </div>

            <div className="gallery123-preview-body">
              {previewItem.photo_url ? (
                <img
                  src={resolveMediaUrl(previewItem.photo_url)}
                  alt={previewItem.title}
                  className="gallery123-preview-img"
                />
              ) : (
                <div className="gallery123-no-data">No photo available.</div>
              )}
            </div>

            {previewItem.caption && (
              <p className="gallery123-preview-caption">{previewItem.caption}</p>
            )}
          </div>
        </div>
      )}

      {/* ---------------- EDIT MODAL ---------------- */}
      {showEditModal && editItem && (
        <div className="gallery123-modal-overlay" onClick={closeEditModal}>
          <div className="gallery123-modal gallery123-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gallery123-modal-header">
              <h3>Edit Photo</h3>
              <button className="gallery123-modal-close" onClick={closeEditModal}>
                &times;
              </button>
            </div>

            <form className="gallery123-modal-body gallery123-edit-modal-body" onSubmit={handleEditSave}>
              <div className="gallery123-edit-grid">
                {/* Left column: text fields */}
                <div className="gallery123-edit-col">
                  <div className="gallery123-form-group">
                    <label>Photo Title</label>
                    <input
                      type="text"
                      name="title"
                      value={editItem.title || ""}
                      onChange={handleEditChange}
                      required
                    />
                  </div>

                  <div className="gallery123-form-row">
                    <div className="gallery123-form-group">
                      <label>Category</label>
                      <select
                        name="category"
                        value={editItem.category || ""}
                        onChange={handleEditChange}
                      >
                        {CATEGORY_OPTIONS.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="gallery123-form-group">
                      <label>Status</label>
                      <select
                        name="status"
                        value={editItem.status || "active"}
                        onChange={handleEditChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="gallery123-form-group">
                    <label>Caption</label>
                    <textarea
                      name="caption"
                      rows="4"
                      value={editItem.caption || ""}
                      onChange={handleEditChange}
                    />
                  </div>
                </div>

                {/* Right column: photo upload + small fixed-size preview */}
                <div className="gallery123-edit-col">
                  <div className="gallery123-form-group">
                    <label>Replace Photo (optional)</label>
                    <input type="file" accept="image/*" onChange={handleEditImageChange} />
                  </div>

                  {editPreview && (
                    <div className="gallery123-edit-preview-box">
                      <img
                        src={
                          editPreview.startsWith("blob:")
                            ? editPreview
                            : resolveMediaUrl(editPreview)
                        }
                        alt="Preview"
                        className="gallery123-edit-preview-img"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="gallery123-modal-footer">
                <button
                  type="button"
                  className="gallery123-btn-cancel"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="gallery123-btn-save" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- DELETE CONFIRM MODAL ---------------- */}
      {showDeleteModal && (
        <div className="gallery123-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="gallery123-modal gallery123-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="gallery123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 className="gallery123-confirm-title">Delete this photo?</h3>
            <p className="gallery123-confirm-text">
              This action cannot be undone. Are you sure you want to delete
              this photo?
            </p>

            <div className="gallery123-confirm-footer">
              <button
                className="gallery123-btn-cancel"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="gallery123-btn-delete"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryTable;